/**
 * Scene Manager - Handles scene lifecycle and transitions
 */

import { Scene } from "@babylonjs/core/scene";
import { Engine } from "../core/Engine";
import { SceneBase } from "./SceneBase";

export class SceneManager {
    private _engine: Engine;
    private _scenes: Map<string, SceneBase> = new Map();
    private _activeSceneName: string | null = null;
    private _activeScene: Scene | null = null;

    public get engine(): Engine {
        return this._engine;
    }

    public get activeScene(): Scene | null {
        return this._activeScene;
    }

    public get activeSceneName(): string | null {
        return this._activeSceneName;
    }

    constructor(engine: Engine) {
        this._engine = engine;
    }

    /**
     * Register a scene with a name
     */
    public registerScene(name: string, scene: SceneBase): void {
        if (this._scenes.has(name)) {
            console.warn(`Scene "${name}" already registered. Overwriting.`);
        }
        this._scenes.set(name, scene);
    }

    /**
     * Unregister and dispose a scene
     */
    public unregisterScene(name: string): void {
        const scene = this._scenes.get(name);
        if (scene) {
            if (this._activeSceneName === name) {
                this._activeScene = null;
                this._activeSceneName = null;
            }
            scene.dispose();
            this._scenes.delete(name);
        }
    }

    /**
     * Switch to a registered scene
     */
    public async switchTo(name: string): Promise<void> {
        const scene = this._scenes.get(name);
        if (!scene) {
            throw new Error(`Scene "${name}" not registered`);
        }

        // Deactivate current scene
        if (this._activeSceneName && this._activeSceneName !== name) {
            const currentScene = this._scenes.get(this._activeSceneName);
            if (currentScene) {
                await currentScene.onDeactivate();
            }
        }

        // Initialize if needed
        if (!scene.isInitialized) {
            await scene.initialize();
        }

        // Activate new scene
        await scene.onActivate();
        this._activeScene = scene.babylonScene;
        this._activeSceneName = name;
    }

    /**
     * Get a registered scene by name
     */
    public getScene(name: string): SceneBase | undefined {
        return this._scenes.get(name);
    }

    /**
     * Check if a scene is registered
     */
    public hasScene(name: string): boolean {
        return this._scenes.has(name);
    }

    /**
     * Update active scene
     */
    public update(deltaTime: number): void {
        if (this._activeSceneName) {
            const scene = this._scenes.get(this._activeSceneName);
            if (scene) {
                scene.update(deltaTime);
            }
        }
    }

    /**
     * Dispose all scenes
     */
    public dispose(): void {
        for (const scene of this._scenes.values()) {
            scene.dispose();
        }
        this._scenes.clear();
        this._activeScene = null;
        this._activeSceneName = null;
    }
}
